import { useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useFamily } from "@/hooks/useFamily";
import { useEntitlements } from "@/hooks/useEntitlements";
import { useImportQuota } from "@/hooks/useImportQuota";
import { UpgradeHint } from "@/components/UpgradeHint";
import { sha256Hex } from "@/lib/crypto";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getISOWeek, addDays, startOfISOWeek } from "date-fns";
import { getDateString } from "@/lib/family-date";

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

import { toast } from "sonner";
import {
  Upload as UploadIcon, Camera, X, Clock, Check, FileText, Loader2,
  BookOpen, AlertCircle, Mic, Video, Image as ImageIcon,
  ChevronDown, ChevronUp, Bell, CalendarDays, Merge, Pencil,
} from "lucide-react";

/* ── v4 Types ── */

interface AISubtask {
  title: string;
  completed: boolean;
  requiresUpload?: "image" | "video" | "audio";
  estimatedMinutes?: number;
}

interface AIHomework {
  type: "homework";
  subject?: string;
  title: string;
  dueDate?: string | null;
  dueDay?: string | null;
  scope: "week_due" | "daily" | "flexible_week";
  confidence?: "low" | "medium" | "high";
  subtasks: AISubtask[];
  originalText?: string;
  enabled: boolean;
}

interface AIReminder {
  type: "reminder";
  title: string;
  remindDate?: string | null;
  category?: "take_along" | "event" | "deadline" | "info" | "action";
  originalText?: string;
  enabled: boolean;
}

interface AIScheduleEntry {
  type: "schedule";
  dayOfWeek: number;
  startTime?: string | null;
  endTime?: string | null;
  title: string;
  enabled: boolean;
}

interface WeekDetection {
  type: "single" | "multiple" | "missing";
  detectedWeeks: number[];
  confidence: "low" | "medium" | "high";
}

interface AIResponseV4 {
  version: 4;
  weekDetection: WeekDetection;
  homework: AIHomework[];
  remindersPreview: AIReminder[];
  planPreview?: { schedule?: AIScheduleEntry[] };
  quality: {
    confidence: "low" | "medium" | "high";
    issues: string[];
  };
}

type Step = "upload" | "week_select" | "preview" | "importing";

interface SiblingInfo {
  id: string;
  name: string;
  avatar_emoji: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  childId: string;
  childName: string;
  familyId?: string | null;
  siblings?: SiblingInfo[];
}

const SCOPE_LABELS: Record<string, string> = {
  week_due: "Frist denne uken",
  daily: "Hver dag",
  flexible_week: "I løpet av uken",
};

const UPLOAD_ICONS: Record<string, typeof Mic> = {
  audio: Mic,
  video: Video,
  image: ImageIcon,
};

const REMINDER_CATEGORY_LABELS: Record<string, string> = {
  take_along: "Ta med",
  event: "Arrangement",
  deadline: "Frist",
  info: "Beskjed",
  action: "Handling",
};

const REMINDER_CATEGORY_ICONS: Record<string, string> = {
  take_along: "🎒",
  event: "📅",
  deadline: "⏰",
  info: "💬",
  action: "✅",
};

const DAY_LABELS = ["Mandag", "Tirsdag", "Onsdag", "Torsdag", "Fredag"];

const DUE_DAY_TO_OFFSET: Record<string, number> = {
  mandag: 0,
  tirsdag: 1,
  onsdag: 2,
  torsdag: 3,
  fredag: 4,
};

/* ── coerceToV4 ── */
function coerceToV4(raw: any): AIResponseV4 {
  if (!raw || typeof raw !== "object") {
    throw new Error("Ugyldig AI-respons");
  }

  // weekDetection
  let weekDetection: WeekDetection;
  if (raw.weekDetection && typeof raw.weekDetection === "object") {
    weekDetection = raw.weekDetection;
  } else if (typeof raw.weekNumber === "number" && raw.weekNumber > 0) {
    weekDetection = { type: "single", detectedWeeks: [raw.weekNumber], confidence: "medium" };
  } else {
    weekDetection = { type: "missing", detectedWeeks: [], confidence: "low" };
  }

  // homework
  const homework: AIHomework[] = (raw.homework || []).map((hw: any) => ({
    ...hw,
    type: "homework",
    enabled: true,
  }));

  // remindersPreview
  const remindersRaw = raw.remindersPreview || raw.reminders || [];
  const remindersPreview: AIReminder[] = remindersRaw.map((r: any) => ({
    ...r,
    type: "reminder",
    enabled: true,
  }));

  // planPreview
  const scheduleRaw = raw.planPreview?.schedule || raw.schedule || [];
  const planPreview = {
    schedule: scheduleRaw.map((s: any) => ({
      ...s,
      type: "schedule",
      enabled: s.enabled !== false,
    })),
  };

  const quality = raw.quality || { confidence: "medium", issues: [] };
  if (!Array.isArray(quality.issues)) quality.issues = [];

  return {
    version: 4,
    weekDetection,
    homework,
    remindersPreview,
    planPreview,
    quality,
  };
}

/* ── Helpers ── */
function getDateForDueDay(weekNumber: number, dueDay: string): string | null {
  const offset = DUE_DAY_TO_OFFSET[dueDay];
  if (offset === undefined) return null;
  // Get the Monday of the given ISO week
  const year = new Date().getFullYear();
  // Jan 4 is always in ISO week 1
  const jan4 = new Date(year, 0, 4);
  const week1Monday = startOfISOWeek(jan4);
  const targetMonday = addDays(week1Monday, (weekNumber - 1) * 7);
  const targetDate = addDays(targetMonday, offset);
  return targetDate.toISOString().slice(0, 10);
}

function normalizeText(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s]/g, "")
    .replace(/\s+/g, " ");
}

export function SchoolPlanImport({ open, onClose, childId, childName, familyId: familyIdProp, siblings = [] }: Props) {
  const { familyId: familyIdFromHook, timezone } = useFamily();
  const familyId = familyIdProp ?? familyIdFromHook;
  const { hasPlus } = useEntitlements(familyId);
  const { used: quotaUsed, limit: quotaLimit, remaining: quotaRemaining } = useImportQuota(familyId);
  const canImport = hasPlus || quotaRemaining > 0;
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>("upload");
  const [response, setResponse] = useState<AIResponseV4 | null>(null);
  const [uploading, setUploading] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [selectedSiblingIds, setSelectedSiblingIds] = useState<string[]>([]);
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);
  const [mergeSelection, setMergeSelection] = useState<{ hwIdx: number; selected: Set<number> } | null>(null);
  const [reminderMergeSelection, setReminderMergeSelection] = useState<Set<number> | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadedFileUrl, setUploadedFileUrl] = useState<string | null>(null);
  const [uploadedStoragePath, setUploadedStoragePath] = useState<string | null>(null);
  const [showSourceImage, setShowSourceImage] = useState(false);

  // Edit dialog state
  const [editingHomework, setEditingHomework] = useState<{ idx: number; data: AIHomework } | null>(null);
  const [editingReminder, setEditingReminder] = useState<{ idx: number; data: AIReminder } | null>(null);
  const [editingSchedule, setEditingSchedule] = useState<{ idx: number; data: AIScheduleEntry } | null>(null);

  // v4 state
  const [selectedWeeks, setSelectedWeeks] = useState<number[]>([]);
  const [weekInput, setWeekInput] = useState<number>(0);

  // Query existing homework weeks for "missing" week suggestion
  const { data: existingWeeks } = useQuery({
    queryKey: ["homework-weeks-existing", childId],
    queryFn: async () => {
      const { data } = await supabase
        .from("homework")
        .select("week_number")
        .eq("child_id", childId);
      return Array.from(new Set((data || []).map((r: any) => r.week_number)));
    },
    enabled: !!childId && step === "week_select",
  });

  // Suggest next available week
  function suggestWeek(): number {
    const currentWeek = getISOWeek(new Date());
    const used = new Set(existingWeeks || []);
    for (let w = currentWeek; w <= currentWeek + 10; w++) {
      const actual = w > 53 ? w - 53 : w;
      if (!used.has(actual)) return actual;
    }
    return currentWeek + 1 > 53 ? 1 : currentWeek + 1;
  }

  async function handleFileUpload(file: File) {
    if (!familyId) return;
    setUploading(true);

    const localUrl = URL.createObjectURL(file);
    setUploadedFileUrl(localUrl);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Ikke innlogget");

      const ext = file.name.split(".").pop() || "pdf";
      const path = `${user.id}/${Date.now()}.${ext}`;

      const { error: uploadErr } = await supabase.storage
        .from("school-plans")
        .upload(path, file, { contentType: file.type });
      if (uploadErr) throw uploadErr;

      setUploadedStoragePath(path);

      const { data: fnData, error: fnErr } = await supabase.functions.invoke(
        "parse-school-plan",
        { body: { filePath: `school-plans/${path}` } }
      );

      if (fnErr) throw fnErr;
      if (fnData?.error) {
        const err = fnData.error;
        throw new Error(typeof err === "string" ? err : err.message || "Ukjent feil");
      }

      const v4 = coerceToV4(fnData);
      setResponse(v4);

      // Routing decision
      if (v4.weekDetection.type === "single" && v4.weekDetection.detectedWeeks.length === 1) {
        setSelectedWeeks(v4.weekDetection.detectedWeeks);
        setStep("preview");
      } else {
        // For multiple or missing: go to week_select
        if (v4.weekDetection.type === "multiple") {
          setSelectedWeeks([...v4.weekDetection.detectedWeeks]);
        } else {
          const suggested = suggestWeek();
          setWeekInput(suggested);
          setSelectedWeeks([]);
        }
        setStep("week_select");
      }
    } catch (err: any) {
      console.error("Upload/parse error:", err);
      toast.error(err.message || "Kunne ikke analysere filen");
    } finally {
      setUploading(false);
    }
  }

  function toggleHomework(idx: number) {
    if (!response) return;
    const updated = { ...response };
    updated.homework = [...updated.homework];
    updated.homework[idx] = { ...updated.homework[idx], enabled: !updated.homework[idx].enabled };
    setResponse(updated);
  }

  function toggleReminder(idx: number) {
    if (!response) return;
    const updated = { ...response };
    updated.remindersPreview = [...updated.remindersPreview];
    updated.remindersPreview[idx] = { ...updated.remindersPreview[idx], enabled: !updated.remindersPreview[idx].enabled };
    setResponse(updated);
  }

  function updateTitle(idx: number, title: string) {
    if (!response) return;
    const updated = { ...response };
    updated.homework = [...updated.homework];
    updated.homework[idx] = { ...updated.homework[idx], title };
    setResponse(updated);
  }

  function updateReminderDate(idx: number, date: string) {
    if (!response) return;
    const updated = { ...response };
    updated.remindersPreview = [...updated.remindersPreview];
    updated.remindersPreview[idx] = { ...updated.remindersPreview[idx], remindDate: date || null };
    setResponse(updated);
  }

  function updateReminderTitle(idx: number, title: string) {
    if (!response) return;
    const updated = { ...response };
    updated.remindersPreview = [...updated.remindersPreview];
    updated.remindersPreview[idx] = { ...updated.remindersPreview[idx], title };
    setResponse(updated);
  }

  // ── Reminder merge logic ──
  function toggleReminderMerge(idx: number) {
    setReminderMergeSelection((prev) => {
      if (!prev) return new Set([idx]);
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }

  function executeReminderMerge() {
    if (!response || !reminderMergeSelection || reminderMergeSelection.size < 2) return;
    const sortedIdxs = Array.from(reminderMergeSelection).sort((a, b) => a - b);
    const mergedTitle = sortedIdxs.map((i) => response.remindersPreview[i].title).join(" + ");
    const firstReminder = response.remindersPreview[sortedIdxs[0]];
    const mergedDate = sortedIdxs.map((i) => response.remindersPreview[i].remindDate).find(Boolean) || null;
    const mergedOriginalText = sortedIdxs
      .map((i) => response.remindersPreview[i].originalText)
      .filter(Boolean)
      .join("\n---\n") || undefined;

    const merged: AIReminder = {
      type: "reminder",
      title: mergedTitle,
      remindDate: mergedDate,
      category: firstReminder.category,
      originalText: mergedOriginalText,
      enabled: true,
    };

    const newReminders = response.remindersPreview.filter((_, i) => !reminderMergeSelection.has(i));
    newReminders.splice(sortedIdxs[0], 0, merged);

    setResponse({ ...response, remindersPreview: newReminders });
    setReminderMergeSelection(null);
    toast.success(`${reminderMergeSelection.size} påminnelser slått sammen`);
  }

  function cancelReminderMerge() {
    setReminderMergeSelection(null);
  }

  // ── Subtask merge logic ──
  function toggleMergeSubtask(hwIdx: number, stIdx: number) {
    setMergeSelection((prev) => {
      if (!prev || prev.hwIdx !== hwIdx) {
        return { hwIdx, selected: new Set([stIdx]) };
      }
      const next = new Set(prev.selected);
      if (next.has(stIdx)) next.delete(stIdx);
      else next.add(stIdx);
      return { hwIdx, selected: next };
    });
  }

  function executeMerge() {
    if (!response || !mergeSelection || mergeSelection.selected.size < 2) return;
    const { hwIdx, selected } = mergeSelection;
    const hw = response.homework[hwIdx];
    const sortedIdxs = Array.from(selected).sort((a, b) => a - b);

    const mergedTitle = sortedIdxs.map((i) => hw.subtasks[i].title).join(" + ");
    const mergedMinutes = sortedIdxs.reduce((sum, i) => sum + (hw.subtasks[i].estimatedMinutes || 0), 0);
    const mergedUpload = sortedIdxs.map((i) => hw.subtasks[i].requiresUpload).find(Boolean) || undefined;

    const mergedSubtask: AISubtask = {
      title: mergedTitle,
      completed: false,
      estimatedMinutes: mergedMinutes || undefined,
      requiresUpload: mergedUpload,
    };

    const newSubtasks = hw.subtasks.filter((_, i) => !selected.has(i));
    newSubtasks.splice(sortedIdxs[0], 0, mergedSubtask);

    const updated = { ...response };
    updated.homework = [...updated.homework];
    updated.homework[hwIdx] = { ...hw, subtasks: newSubtasks };
    setResponse(updated);
    setMergeSelection(null);
    toast.success(`${selected.size} deloppgaver slått sammen`);
  }

  function cancelMerge() {
    setMergeSelection(null);
  }

  // ── Import ──
  async function handleImport() {
    if (!response || !childId || !familyId) return;
    setStep("importing");

    const { data: { user } } = await supabase.auth.getUser();
    const userId = user?.id ?? null;

    const allChildIds = [childId, ...selectedSiblingIds];
    const allChildNames = [childName, ...selectedSiblingIds.map((id) => siblings.find((s) => s.id === id)?.name || "Barn")];

    const enabledHomework = response.homework.filter((hw) => hw.enabled);
    const enabledReminders = response.remindersPreview.filter((r) => r.enabled);
    const enabledSchedule = (response.planPreview?.schedule || []).filter((s) => s.enabled);

    // Calculate total ops for progress
    const totalOps = (enabledHomework.length * selectedWeeks.length + enabledReminders.length + enabledSchedule.length) * allChildIds.length;
    let completed = 0;
    let skipped = 0;
    const skippedDueDateItems: string[] = [];

    for (const cid of allChildIds) {
      for (const week of selectedWeeks) {
        const batchId = crypto.randomUUID();

        // Save school_plan_imports record per child×week
        if (uploadedStoragePath) {
          const { error: importErr } = await supabase.from("school_plan_imports" as any).insert({
            family_id: familyId,
            child_id: cid,
            storage_path: uploadedStoragePath,
            week_number: week,
            import_batch_id: batchId,
            created_by: userId,
            parse_confidence: response.quality?.confidence || null,
            parse_issues: response.quality?.issues?.length ? response.quality.issues : null,
          } as any);
          if (importErr) {
            console.error("Failed to save school plan import record:", importErr);
          }
        }

        // Import homework for this week
        for (const hw of enabledHomework) {
          // Determine target weeks for this item
          if (hw.dueDate) {
            // Has explicit dueDate: check if its ISO week matches this target week
            const dueDateWeek = getISOWeek(new Date(hw.dueDate));
            if (dueDateWeek !== week) {
              // Skip: dueDate is for a different week
              if (!skippedDueDateItems.includes(hw.title)) {
                skippedDueDateItems.push(hw.title);
              }
              completed++;
              setImportProgress(Math.round((completed / totalOps) * 100));
              continue;
            }
          }

          // Compute effective dueDate from dueDay if available
          let effectiveDueDate = hw.dueDate || null;
          if (!effectiveDueDate && hw.dueDay) {
            effectiveDueDate = getDateForDueDay(week, hw.dueDay);
          }

          const rawText = hw.subtasks.map((st) => st.title).join(" ");
          const fingerprintInput = `${cid}|${week}|${normalizeText(rawText)}`;
          const fingerprint = await sha256Hex(fingerprintInput);

          try {
            const { data: hwData, error: hwErr } = await supabase
              .from("homework" as any)
              .insert({
                child_id: cid,
                family_id: familyId,
                week_number: week,
                subject: hw.subject || null,
                title: hw.title,
                due_date: effectiveDueDate,
                scope: hw.scope,
                confidence: hw.confidence || null,
                source_fingerprint: fingerprint,
                import_batch_id: batchId,
                created_by: userId,
                original_text: hw.originalText || null,
              } as any)
              .select("id")
              .single();

            if (hwErr) {
              if (hwErr.code === "23505") skipped++;
              else console.warn("Failed to create homework:", hwErr);
              completed++;
              setImportProgress(Math.round((completed / totalOps) * 100));
              continue;
            }

            if (hwData && hw.subtasks.length > 0) {
              const subtasks = hw.subtasks.map((st, i) => ({
                homework_id: (hwData as any).id,
                title: st.title,
                completed: false,
                requires_upload: st.requiresUpload || null,
                estimated_minutes: st.estimatedMinutes || null,
                sort_order: i,
              }));
              await supabase.from("homework_subtasks" as any).insert(subtasks as any);
            }
          } catch (err) {
            console.warn("Failed to create homework:", err);
          }

          completed++;
          setImportProgress(Math.round((completed / totalOps) * 100));
        }

        // Import reminders / tasks / events (only for first week to avoid duplication)
        // MVP: action → task_items, event → child_calendar_events, only notice/bring/deadline → reminders
        if (week === selectedWeeks[0]) {
          const defaultDateStr = getDateForDueDay(week, "mandag") ?? getDateString(new Date(), timezone);
          for (const r of enabledReminders) {
            const category = r.category || "info";
            try {
              if (category === "action") {
                const taskDate = r.remindDate || defaultDateStr;
                await supabase
                  .from("task_items" as any)
                  .insert({
                    child_id: cid,
                    family_id: familyId,
                    date: taskDate,
                    title: r.title,
                    status: "pending",
                    duration_seconds: 600,
                    completion_points: 5,
                    created_by: userId,
                    category: null,
                    description: r.originalText || null,
                  } as any);
              } else if (category === "event") {
                const eventDate = r.remindDate || defaultDateStr;
                await supabase
                  .from("child_calendar_events" as any)
                  .insert({
                    child_id: cid,
                    family_id: familyId,
                    event_type: "activity",
                    title: r.title,
                    description: r.originalText || null,
                    day_of_week: null,
                    specific_date: eventDate,
                    start_time: null,
                    end_time: null,
                    recurring: false,
                    source: "school_plan_import",
                    source_ref: null,
                    metadata: {},
                    created_by: userId,
                  } as any);
              } else {
                // notice, take_along, deadline (and any other) → reminders
                await supabase
                  .from("reminders")
                  .insert({
                    child_id: cid,
                    family_id: familyId,
                    title: r.title,
                    remind_date: r.remindDate || null,
                    import_batch_id: batchId,
                    created_by: userId,
                    original_text: r.originalText || null,
                    category,
                  } as any);
              }
            } catch (err) {
              console.warn("Failed to create reminder/task/event:", err);
            }

            completed++;
            setImportProgress(Math.round((completed / totalOps) * 100));
          }
        }

        // Import schedule entries (only for first week to avoid duplication)
        if (week === selectedWeeks[0] && enabledSchedule.length > 0) {
          for (const s of enabledSchedule) {
            try {
              // Calculate valid_from (Monday) and valid_until (Sunday) for the selected week
              const weekNum = selectedWeeks[0];
              const importYear = new Date().getFullYear();
              const jan4 = new Date(importYear, 0, 4);
              const week1Monday = startOfISOWeek(jan4);
              const validFromDate = addDays(week1Monday, (weekNum - 1) * 7);
              const validUntilDate = addDays(validFromDate, 6);
              const validFrom = validFromDate.toISOString().slice(0, 10);
              const validUntil = validUntilDate.toISOString().slice(0, 10);

              await supabase
                .from("child_calendar_events" as any)
                .insert({
                  child_id: cid,
                  family_id: familyId,
                  event_type: "school",
                  title: s.title,
                  description: null,
                  day_of_week: s.dayOfWeek,
                  specific_date: null,
                  start_time: s.startTime || null,
                  end_time: s.endTime || null,
                  recurring: true,
                  source: "school_plan_import",
                  source_ref: null,
                  import_batch_id: batchId,
                  metadata: {},
                  created_by: userId,
                  valid_from: validFrom,
                  valid_until: validUntil,
                } as any);
            } catch (err) {
              console.warn("Failed to create schedule event:", err);
            }
            completed++;
            setImportProgress(Math.round((completed / totalOps) * 100));
          }
        }
      }
    }

    // Warnings
    if (skipped > 0) {
      toast.info(`${skipped} lekser fantes allerede og ble hoppet over`);
    }
    if (skippedDueDateItems.length > 0) {
      toast.warning(`${skippedDueDateItems.length} oppgave(r) ble hoppet over fordi fristen ikke matcher valgte uker`);
    }

    const importedHomework = enabledHomework.length * selectedWeeks.length - skipped - skippedDueDateItems.length;
    const importedTasks = enabledReminders.filter((r) => (r.category || "info") === "action").length * allChildIds.length;
    const importedEvents = enabledReminders.filter((r) => (r.category || "info") === "event").length * allChildIds.length;
    const importedReminders = enabledReminders.filter((r) => {
      const c = r.category || "info";
      return c !== "action" && c !== "event";
    }).length * allChildIds.length;
    const importedSchedule = enabledSchedule.length * allChildIds.length;
    const names = allChildNames.join(" og ");
    const parts: string[] = [];
    if (importedHomework > 0) parts.push(`${importedHomework} lekser`);
    if (importedTasks > 0) parts.push(`${importedTasks} oppgaver`);
    if (importedEvents > 0) parts.push(`${importedEvents} hendelser`);
    if (importedReminders > 0) parts.push(`${importedReminders} påminnelser`);
    if (importedSchedule > 0) parts.push(`${importedSchedule} timeplanelementer`);
    if (parts.length > 0) {
      toast.success(`${parts.join(", ")} importert for ${names}`);
    }

    queryClient.invalidateQueries({ queryKey: ["homework"] });
    queryClient.invalidateQueries({ queryKey: ["homework-weeks"] });
    queryClient.invalidateQueries({ queryKey: ["reminders"] });
    queryClient.invalidateQueries({ queryKey: ["task-items"] });
    queryClient.invalidateQueries({ queryKey: ["child-calendar-events"] });
    queryClient.invalidateQueries({ queryKey: ["school-plan-imports"] });
    queryClient.invalidateQueries({ queryKey: ["import-quota"] });
    handleClose();
  }

  function handleClose() {
    setStep("upload");
    setResponse(null);
    setUploading(false);
    setImportProgress(0);
    setSelectedSiblingIds([]);
    setExpandedIdx(null);
    setMergeSelection(null);
    setReminderMergeSelection(null);
    setSelectedWeeks([]);
    setWeekInput(0);
    if (uploadedFileUrl) {
      URL.revokeObjectURL(uploadedFileUrl);
      setUploadedFileUrl(null);
    }
    setUploadedStoragePath(null);
    setShowSourceImage(false);
    onClose();
  }

  const enabledHomeworkCount = response?.homework.filter((hw) => hw.enabled).length || 0;
  // Split reminders: events go to calendar section, rest stay as reminders
  const eventReminders = (response?.remindersPreview || []).map((r, idx) => ({ ...r, origIdx: idx })).filter((r) => r.category === "event");
  const nonEventReminders = (response?.remindersPreview || []).map((r, idx) => ({ ...r, origIdx: idx })).filter((r) => r.category !== "event");
  const enabledEventCount = eventReminders.filter((r) => r.enabled).length;
  const enabledReminderCount = nonEventReminders.filter((r) => r.enabled).length;
  const enabledScheduleCount = response?.planPreview?.schedule?.filter((s) => s.enabled).length || 0;
  const enabledCalendarCount = enabledScheduleCount + enabledEventCount;
  const importEligible = enabledHomeworkCount > 0 || enabledReminderCount > 0 || enabledCalendarCount > 0;

  return (
    <Sheet open={open} onOpenChange={(o) => !o && handleClose()}>
      <SheetContent side="bottom" className="h-[90vh] overflow-y-auto rounded-t-2xl">
        <SheetHeader className="pb-2">
          <SheetTitle className="text-lg font-display">
            {step === "upload" && "Last opp ukeplan"}
            {step === "week_select" && "Velg uke"}
            {step === "preview" && `📚 Uke ${selectedWeeks.join(" og ")} – ${childName}`}
            {step === "importing" && "Importerer…"}
          </SheetTitle>
          <SheetDescription>
            {step === "upload" && "Last opp en PDF eller ta bilde av ukeplanen fra skolen"}
            {step === "week_select" && "Velg hvilke uker du vil importere til"}
            {step === "preview" && "Gjennomgå lekser, påminnelser og meldinger"}
            {step === "importing" && "Innholdet opprettes nå"}
          </SheetDescription>
        </SheetHeader>

        {!canImport ? (
          <div className="py-8 space-y-3">
            <div className="rounded-xl border border-border bg-muted/30 p-4 space-y-2 text-center">
              <p className="font-display font-semibold text-sm">
                Ukeplan-import for {childName}
              </p>
              <p className="text-xs text-muted-foreground">
                Du har brukt alle {quotaLimit} gratis importer. For å fortsette å laste opp ukeplaner må du oppgradere til Flyt Pluss.
              </p>
            </div>
            <UpgradeHint
              title="Se hva Flyt Pluss gir"
              description="Med Flyt Pluss får du ubegrenset import av ukeplaner og automatisk oppretting av lekser."
              targetPlan="plus"
              ctaLabel="Oppgrader til Flyt Pluss"
              useCheckout
              familyId={familyId ?? undefined}
            />
          </div>
        ) : (
          <>
            {/* Quota banner for free users */}
            {!hasPlus && step === "upload" && (
              <div className="mx-auto max-w-sm rounded-lg border border-primary/20 bg-primary/5 px-3 py-2 mb-2">
                <p className="text-xs text-center text-muted-foreground">
                  Gratis import <span className="font-semibold text-foreground">{quotaUsed + 1} av {quotaLimit}</span> — {quotaRemaining - 1 === 0 ? "siste gratis import" : `${quotaRemaining - 1} igjen etter denne`}
                </p>
              </div>
            )}
            {/* ── Upload ── */}
            {step === "upload" && (
              <div className="flex flex-col items-center gap-6 py-8">
                <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center">
                  <FileText className="h-10 w-10 text-primary" />
                </div>

                {uploading ? (
                  <div className="flex flex-col items-center gap-3">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    <p className="text-sm text-muted-foreground">Analyserer ukeplanen med AI…</p>
                  </div>
                ) : (
                  <>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,image/*"
                      capture="environment"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleFileUpload(file);
                      }}
                    />
                    <div className="flex gap-3 w-full max-w-xs">
                      <Button
                        variant="outline"
                        className="flex-1 gap-2"
                        onClick={() => {
                          if (fileInputRef.current) {
                            fileInputRef.current.removeAttribute("capture");
                            fileInputRef.current.click();
                          }
                        }}
                      >
                        <UploadIcon className="h-4 w-4" />
                        Velg fil
                      </Button>
                      <Button
                        variant="outline"
                        className="flex-1 gap-2"
                        onClick={() => {
                          if (fileInputRef.current) {
                            fileInputRef.current.setAttribute("capture", "environment");
                            fileInputRef.current.click();
                          }
                        }}
                      >
                        <Camera className="h-4 w-4" />
                        Ta bilde
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground text-center max-w-xs">
                      PDF eller bilde av ukeplan fra skolen. AI henter ut lekser, påminnelser og meldinger.
                    </p>
                  </>
                )}
              </div>
            )}

            {/* ── Week Select ── */}
            {step === "week_select" && response && (
              <div className="space-y-6 py-6">
                {response.weekDetection.type === "multiple" ? (
                  <>
                    <p className="text-sm text-muted-foreground">
                      Planen ser ut til å gjelde flere uker. Velg hvilke uker du vil importere til.
                    </p>
                    <div className="space-y-2">
                      {response.weekDetection.detectedWeeks.map((w) => (
                        <label key={w} className="flex items-center gap-3 cursor-pointer text-sm">
                          <Checkbox
                            checked={selectedWeeks.includes(w)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedWeeks((prev) => [...prev, w]);
                              } else {
                                setSelectedWeeks((prev) => prev.filter((x) => x !== w));
                              }
                            }}
                          />
                          <span className="font-medium">Uke {w}</span>
                        </label>
                      ))}
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-muted-foreground">
                      Vi fant ikke ukenummer i planen. Velg uke.
                    </p>
                    <div className="flex items-center gap-3">
                      <label className="text-sm font-medium">Uke:</label>
                      <Input
                        type="number"
                        min={1}
                        max={53}
                        value={weekInput || ""}
                        onChange={(e) => setWeekInput(parseInt(e.target.value) || 0)}
                        className="w-24"
                      />
                    </div>
                  </>
                )}

                <Button
                  className="w-full"
                  disabled={
                    response.weekDetection.type === "multiple"
                      ? selectedWeeks.length === 0
                      : weekInput < 1 || weekInput > 53
                  }
                  onClick={() => {
                    if (response.weekDetection.type !== "multiple") {
                      setSelectedWeeks([weekInput]);
                    }
                    setStep("preview");
                  }}
                >
                  Neste
                </Button>
              </div>
            )}

            {/* ── Preview ── */}
            {step === "preview" && response && (
              <div className="space-y-4 pb-24">
                {/* Source image viewer */}
                {uploadedFileUrl && (
                  <div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full gap-2 text-xs"
                      onClick={() => setShowSourceImage(!showSourceImage)}
                    >
                      <ImageIcon className="h-3.5 w-3.5" />
                      {showSourceImage ? "Skjul original ukeplan" : "Vis original ukeplan"}
                    </Button>
                    {showSourceImage && (
                      <div className="mt-2 rounded-lg border overflow-hidden bg-muted/30">
                        <img
                          src={uploadedFileUrl}
                          alt="Opplastet ukeplan"
                          className="w-full max-h-[50vh] object-contain"
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Low confidence banner */}
                {response.quality.confidence === "low" && (
                  <Card className="border-amber-500/50 bg-amber-50 dark:bg-amber-950/20">
                    <CardContent className="p-3 flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                      <p className="text-sm text-amber-800 dark:text-amber-300 font-medium">
                        Lav konfidens – vennligst gjennomgå før import
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Quality issues */}
                {response.quality.issues.length > 0 && (
                  <Card className="border-amber-500/50 bg-amber-50 dark:bg-amber-950/20">
                    <CardContent className="p-3 flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                      <div className="text-sm">
                        <p className="font-medium text-amber-800 dark:text-amber-300">Mulige problemer</p>
                        {response.quality.issues.map((issue, i) => (
                          <p key={i} className="text-amber-700 dark:text-amber-400/80 text-xs mt-0.5">{issue}</p>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* ── Homework list ── */}
                {response.homework.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold mb-2 text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                      <BookOpen className="h-3.5 w-3.5" /> Lekser ({enabledHomeworkCount}/{response.homework.length})
                    </h3>
                    <div className="space-y-1.5">
                      {response.homework.map((hw, idx) => {
                        const totalMinutes = hw.subtasks.reduce((sum, st) => sum + (st.estimatedMinutes || 0), 0);
                        return (
                          <div key={idx} className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 transition-opacity ${!hw.enabled ? "opacity-40 bg-muted/30" : "bg-background"}`}>
                            <button onClick={() => toggleHomework(idx)} className={`h-5 w-5 rounded-full border-2 flex items-center justify-center shrink-0 ${hw.enabled ? "border-primary bg-primary text-primary-foreground" : "border-muted-foreground"}`}>
                              {hw.enabled && <Check className="h-3 w-3" />}
                            </button>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{hw.title}</p>
                              <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                                {hw.subject && <Badge variant="secondary" className="text-[10px] px-1.5 py-0">{hw.subject}</Badge>}
                                <Badge variant="outline" className="text-[10px] px-1.5 py-0">{SCOPE_LABELS[hw.scope]}</Badge>
                                {hw.dueDay && <span className="text-[10px] text-muted-foreground">📅 {hw.dueDay}</span>}
                                {hw.dueDate && <span className="text-[10px] text-muted-foreground">📅 {hw.dueDate}</span>}
                                {totalMinutes > 0 && <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground"><Clock className="h-3 w-3" /> ~{totalMinutes} min</span>}
                                {hw.subtasks.length > 0 && <span className="text-[10px] text-muted-foreground">{hw.subtasks.length} deloppgave{hw.subtasks.length !== 1 ? "r" : ""}</span>}
                              </div>
                            </div>
                            <button onClick={() => setEditingHomework({ idx, data: { ...hw, subtasks: hw.subtasks.map(s => ({ ...s })) } })} className="text-muted-foreground hover:text-foreground shrink-0 p-1 rounded-md hover:bg-muted transition-colors">
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* ── Calendar (Schedule + Events) ── */}
                {((response.planPreview?.schedule?.length ?? 0) > 0 || eventReminders.length > 0) && (
                  <div>
                    <h3 className="text-sm font-semibold mb-2 text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                      <CalendarDays className="h-3.5 w-3.5" /> Kalender ({enabledCalendarCount}/{(response.planPreview?.schedule || []).length + eventReminders.length})
                    </h3>
                    {/* Schedule entries by day */}
                    {(response.planPreview?.schedule?.length ?? 0) > 0 && (
                      <div className="space-y-2">
                        {[0, 1, 2, 3, 4].map((day) => {
                          const dayEntries = (response.planPreview?.schedule || []).map((s, origIdx) => ({ ...s, origIdx })).filter((s) => s.dayOfWeek === day);
                          if (dayEntries.length === 0) return null;
                          return (
                            <div key={day}>
                              <p className="text-xs font-medium text-muted-foreground mb-1">{DAY_LABELS[day]}</p>
                              <div className="space-y-1">
                                {dayEntries.map((s) => (
                                  <div key={s.origIdx} className={`flex items-center gap-2.5 rounded-lg border px-3 py-2 transition-opacity ${!s.enabled ? "opacity-40 bg-muted/30" : "bg-background"}`}>
                                    <button onClick={() => { const updated = { ...response }; updated.planPreview!.schedule![s.origIdx].enabled = !s.enabled; setResponse({ ...updated }); }} className={`h-4 w-4 rounded-full border-2 flex items-center justify-center shrink-0 ${s.enabled ? "border-primary bg-primary text-primary-foreground" : "border-muted-foreground"}`}>
                                      {s.enabled && <Check className="h-2.5 w-2.5" />}
                                    </button>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium truncate">{s.title}</p>
                                      {(s.startTime || s.endTime) && <p className="text-[11px] text-muted-foreground">{s.startTime || "?"} – {s.endTime || "?"}</p>}
                                    </div>
                                    <button onClick={() => setEditingSchedule({ idx: s.origIdx, data: { ...s } })} className="text-muted-foreground hover:text-foreground shrink-0 p-1 rounded-md hover:bg-muted transition-colors">
                                      <Pencil className="h-3.5 w-3.5" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Event reminders shown under calendar */}
                    {eventReminders.length > 0 && (
                      <div className={`space-y-1.5 ${(response.planPreview?.schedule?.length ?? 0) > 0 ? "mt-3 pt-3 border-t border-border" : ""}`}>
                        <p className="text-xs font-medium text-muted-foreground mb-1">Hendelser</p>
                        {eventReminders.map((r) => (
                          <div key={r.origIdx} className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 transition-opacity ${!r.enabled ? "opacity-40 bg-muted/30" : "bg-background"}`}>
                            <button onClick={() => toggleReminder(r.origIdx)} className={`h-5 w-5 rounded-full border-2 flex items-center justify-center shrink-0 ${r.enabled ? "border-primary bg-primary text-primary-foreground" : "border-muted-foreground"}`}>
                              {r.enabled && <Check className="h-3 w-3" />}
                            </button>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{r.title}</p>
                              <div className="flex items-center gap-1.5 mt-0.5">
                                <Badge variant="secondary" className="text-[10px] px-1.5 py-0">📅 Arrangement</Badge>
                                {r.remindDate && <span className="text-[10px] text-muted-foreground">📅 {r.remindDate}</span>}
                              </div>
                            </div>
                            <button onClick={() => setEditingReminder({ idx: r.origIdx, data: { ...r } })} className="text-muted-foreground hover:text-foreground shrink-0 p-1 rounded-md hover:bg-muted transition-colors">
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* ── Reminders (non-event only) ── */}
                {nonEventReminders.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold mb-2 text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                      <Bell className="h-3.5 w-3.5" /> Påminnelser ({enabledReminderCount}/{nonEventReminders.length})
                    </h3>
                    <div className="space-y-1.5">
                      {nonEventReminders.map((r) => {
                        const emoji = REMINDER_CATEGORY_ICONS[r.category || "info"] || "💬";
                        const categoryLabel = REMINDER_CATEGORY_LABELS[r.category || "info"] || "Beskjed";
                        return (
                          <div key={r.origIdx} className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 transition-opacity ${!r.enabled ? "opacity-40 bg-muted/30" : "bg-background"}`}>
                            <button onClick={() => toggleReminder(r.origIdx)} className={`h-5 w-5 rounded-full border-2 flex items-center justify-center shrink-0 ${r.enabled ? "border-primary bg-primary text-primary-foreground" : "border-muted-foreground"}`}>
                              {r.enabled && <Check className="h-3 w-3" />}
                            </button>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{r.title}</p>
                              <div className="flex items-center gap-1.5 mt-0.5">
                                <Badge variant="secondary" className="text-[10px] px-1.5 py-0">{emoji} {categoryLabel}</Badge>
                                {r.remindDate && <span className="text-[10px] text-muted-foreground">📅 {r.remindDate}</span>}
                              </div>
                            </div>
                            <button onClick={() => setEditingReminder({ idx: r.origIdx, data: { ...r } })} className="text-muted-foreground hover:text-foreground shrink-0 p-1 rounded-md hover:bg-muted transition-colors">
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Sibling selection */}
                {siblings.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold mb-2 text-muted-foreground uppercase tracking-wide">
                      Importer for flere barn?
                    </h3>
                    <div className="space-y-1.5">
                      {siblings.map((s) => (
                        <label key={s.id} className="flex items-center gap-2 cursor-pointer text-sm">
                          <Checkbox
                            checked={selectedSiblingIds.includes(s.id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedSiblingIds((prev) => [...prev, s.id]);
                              } else {
                                setSelectedSiblingIds((prev) => prev.filter((id) => id !== s.id));
                              }
                            }}
                          />
                          <span>{s.avatar_emoji} {s.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Fixed bottom bar */}
                <div className="fixed bottom-0 left-0 right-0 p-4 bg-background border-t z-50">
                  <div className="flex items-center justify-between max-w-lg mx-auto gap-3">
                    <p className="text-sm text-muted-foreground">
                      {[
                        enabledHomeworkCount > 0 && `${enabledHomeworkCount} lekser`,
                        enabledCalendarCount > 0 && `${enabledCalendarCount} kalender`,
                        enabledReminderCount > 0 && `${enabledReminderCount} påminnelser`,
                      ].filter(Boolean).join(" + ")}
                      {selectedWeeks.length > 1 ? ` × ${selectedWeeks.length} uker` : ""}
                      {selectedSiblingIds.length > 0 ? ` × ${1 + selectedSiblingIds.length} barn` : ""} valgt
                    </p>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={handleClose}>
                        Avbryt
                      </Button>
                      <Button size="sm" onClick={handleImport} disabled={!importEligible}>
                        Importer
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── Importing ── */}
            {step === "importing" && (
              <div className="flex flex-col items-center gap-6 py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <div className="w-full max-w-xs space-y-2">
                  <Progress value={importProgress} className="h-2" />
                  <p className="text-xs text-center text-muted-foreground">
                    {importProgress}% fullført
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </SheetContent>

      {/* ── Edit Homework Dialog ── */}
      <Dialog open={!!editingHomework} onOpenChange={(o) => !o && setEditingHomework(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Rediger lekse</DialogTitle>
          </DialogHeader>
          {editingHomework && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs">Tittel</Label>
                <Input value={editingHomework.data.title} onChange={(e) => setEditingHomework({ ...editingHomework, data: { ...editingHomework.data, title: e.target.value } })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Fag</Label>
                  <Input value={editingHomework.data.subject || ""} onChange={(e) => setEditingHomework({ ...editingHomework, data: { ...editingHomework.data, subject: e.target.value || undefined } })} placeholder="Valgfritt" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Frist (dag)</Label>
                  <Input value={editingHomework.data.dueDay || ""} onChange={(e) => setEditingHomework({ ...editingHomework, data: { ...editingHomework.data, dueDay: e.target.value || undefined } })} placeholder="f.eks. tirsdag" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Frist (dato)</Label>
                <Input type="date" value={editingHomework.data.dueDate || ""} onChange={(e) => setEditingHomework({ ...editingHomework, data: { ...editingHomework.data, dueDate: e.target.value || null } })} />
              </div>
              {editingHomework.data.subtasks.length > 0 && (
                <div className="space-y-1.5">
                  <Label className="text-xs">Deloppgaver</Label>
                  <div className="space-y-1.5">
                    {editingHomework.data.subtasks.map((st, stIdx) => (
                      <div key={stIdx} className="flex items-center gap-2">
                        <Input
                          value={st.title}
                          onChange={(e) => {
                            const newSubtasks = [...editingHomework.data.subtasks];
                            newSubtasks[stIdx] = { ...newSubtasks[stIdx], title: e.target.value };
                            setEditingHomework({ ...editingHomework, data: { ...editingHomework.data, subtasks: newSubtasks } });
                          }}
                          className="text-sm"
                        />
                        <button
                          onClick={() => {
                            const newSubtasks = editingHomework.data.subtasks.filter((_, i) => i !== stIdx);
                            setEditingHomework({ ...editingHomework, data: { ...editingHomework.data, subtasks: newSubtasks } });
                          }}
                          className="text-muted-foreground hover:text-destructive shrink-0"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingHomework(null)}>Avbryt</Button>
            <Button onClick={() => {
              if (!editingHomework || !response) return;
              const updated = { ...response };
              updated.homework = [...updated.homework];
              updated.homework[editingHomework.idx] = editingHomework.data;
              setResponse(updated);
              setEditingHomework(null);
            }}>Lagre</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Edit Schedule Dialog ── */}
      <Dialog open={!!editingSchedule} onOpenChange={(o) => !o && setEditingSchedule(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Rediger timeplanpost</DialogTitle>
          </DialogHeader>
          {editingSchedule && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs">Tittel</Label>
                <Input value={editingSchedule.data.title} onChange={(e) => setEditingSchedule({ ...editingSchedule, data: { ...editingSchedule.data, title: e.target.value } })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Starttid</Label>
                  <Input type="time" value={editingSchedule.data.startTime || ""} onChange={(e) => setEditingSchedule({ ...editingSchedule, data: { ...editingSchedule.data, startTime: e.target.value || null } })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Sluttid</Label>
                  <Input type="time" value={editingSchedule.data.endTime || ""} onChange={(e) => setEditingSchedule({ ...editingSchedule, data: { ...editingSchedule.data, endTime: e.target.value || null } })} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Dag</Label>
                <select
                  value={editingSchedule.data.dayOfWeek}
                  onChange={(e) => setEditingSchedule({ ...editingSchedule, data: { ...editingSchedule.data, dayOfWeek: parseInt(e.target.value) } })}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  {DAY_LABELS.map((label, i) => <option key={i} value={i}>{label}</option>)}
                </select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingSchedule(null)}>Avbryt</Button>
            <Button onClick={() => {
              if (!editingSchedule || !response) return;
              const updated = { ...response };
              updated.planPreview!.schedule![editingSchedule.idx] = editingSchedule.data;
              setResponse({ ...updated });
              setEditingSchedule(null);
            }}>Lagre</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Edit Reminder Dialog ── */}
      <Dialog open={!!editingReminder} onOpenChange={(o) => !o && setEditingReminder(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Rediger påminnelse</DialogTitle>
          </DialogHeader>
          {editingReminder && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs">Tittel</Label>
                <Input value={editingReminder.data.title} onChange={(e) => setEditingReminder({ ...editingReminder, data: { ...editingReminder.data, title: e.target.value } })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Dato</Label>
                <Input type="date" value={editingReminder.data.remindDate || ""} onChange={(e) => setEditingReminder({ ...editingReminder, data: { ...editingReminder.data, remindDate: e.target.value || null } })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Kategori</Label>
                <select
                  value={editingReminder.data.category || "info"}
                  onChange={(e) => setEditingReminder({ ...editingReminder, data: { ...editingReminder.data, category: e.target.value as AIReminder["category"] } })}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  {Object.entries(REMINDER_CATEGORY_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>{REMINDER_CATEGORY_ICONS[key]} {label}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingReminder(null)}>Avbryt</Button>
            <Button onClick={() => {
              if (!editingReminder || !response) return;
              const updated = { ...response };
              updated.remindersPreview = [...updated.remindersPreview];
              updated.remindersPreview[editingReminder.idx] = editingReminder.data;
              setResponse(updated);
              setEditingReminder(null);
            }}>Lagre</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Sheet>
  );
}
